import { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Upload, Trash2, Search, LayoutGrid, List, Pencil, Package, Layers,
  FolderPlus, Link2, Plus, ChevronLeft, ChevronRight, Archive, Radio, Loader2,
} from 'lucide-react'
import { toast } from 'sonner'
import { ConfirmDialog } from '@meno/ui/components/ui/confirm-dialog'
import { Card, CardContent } from '@meno/ui/components/ui/card'
import { Button } from '@meno/ui/components/ui/button'
import { Skeleton } from '@meno/ui/components/ui/skeleton'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@meno/ui/components/ui/select'
import { Input } from '@meno/ui/components/ui/input'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@meno/ui/components/ui/table'
import api, {
  getMedia, uploadMedia, updateMedia, deleteMedia, archiveMedia, restoreMedia,
  getPlaylists, createPlaylist,
  updatePlaylist, deletePlaylist, getProducts, getBrandAssets, BACKEND_URL,
  createMediaFromPdf, deleteInteractiveMenu, type QrPosition,
  getAudios, deleteAudio, restoreAudio, publishMedia, publishMenu, uploadAudio, updateAudio,
  getFolders, createFolder, deleteFolder, assignFolder,
} from '@meno/ui/lib/api'
import { usePageHeader } from '@meno/ui/components/layout/PageHeader'
import { usePermissions } from '@meno/ui/hooks/usePermissions'
import { useTaskCenter } from '@/contexts/TaskCenterContext'
import { formatBytes, formatDate } from '@meno/ui/lib/utils'
import CreateLinkDialog, { type InteractiveLink } from '@/components/CreateLinkDialog'
import AssetTabs from '@/components/AssetTabs'
import EmptyState from '@/components/EmptyState'
import AssetPreviewDialog, { type PreviewAsset } from '@/components/AssetPreviewDialog'
import PlaylistDialog from '@/components/PlaylistDialog'
import { PublishDialog } from '@/components/PublishDialog'
import { companionsToPayload, type Companions } from '@/components/companionsModel'
// Cards, dialogs, types + helpers moved to ./media-page/*. The dead
// SectionHeading function was deleted — the All-tab grid is flat (no
// dividers) and the helper was never wired in.
import {
  THUMB_CACHE_PREFIX, SLOT_LABELS, FOLDERS_STORAGE_KEY, ASSET_FOLDER_MAP_KEY,
  loadFolders, normalizeMediaUrl, captureVideoThumbnail,
  uploadLimitError,
  loadAssetFolderMap, assetMapKey,
  type MediaFile, type AudioFile, type Playlist, type EventFields, type ProductLite, type Folder,
  type AssetKind,
} from './media-page/utils'
import {
  MediaThumbnail, GridCard, FolderCard, LinkCard, TypeBadge, AudioCard,
} from './media-page/cards'
import {
  UploadConfirmDialog, RenameDialog, NewFolderDialog,
  type UploadPreview, type PdfUploadMode,
} from './media-page/dialogs'
export default function MediaPage() {
  const { t, i18n } = useTranslation()
  usePageHeader({ title: t('assets.title', 'Asset') })
  const { runUpload } = useTaskCenter()
  const { can } = usePermissions()
  const canUpload = can('media:upload')
  const canDelete = can('media:delete')
  const navigate = useNavigate()
  const location = useLocation()
  // Four top-level tabs.
  //   all       — all asset types, only place that exposes "New Folder"
  //   media     — images / videos / gifs (auto-locks filterType to 'image' or 'video')
  //   documents — PDFs + their derived playlists (Upload triggers PDF flow)
  //   links     — external links (placeholder until backend lands)
  // Initial tab honours a #hash in the URL so the Brand Assets page can
  // return us to a specific tab via /management/media#documents etc.
  const initialTab = (() => {
    const h = location.hash.replace(/^#/, '')
    return h === 'media' || h === 'documents' || h === 'links' || h === 'archive' || h === 'all' ? h : 'all'
  })() as 'all' | 'media' | 'documents' | 'links' | 'archive'
  const [activeTab,     setActiveTab]     = useState<'all' | 'media' | 'documents' | 'links' | 'archive'>(initialTab)
  const [files,         setFiles]         = useState<MediaFile[]>([])
  // Archive (soft-deleted) media + audio. Loaded on mount so the tab badge is
  // accurate, and kept in sync optimistically as items are archived/restored.
  // Audio itself is managed in the Audio Hub now; only its archived rows still
  // surface here (the Archive is the shared restore/permanent-delete surface).
  const [archivedFiles,  setArchivedFiles]  = useState<MediaFile[]>([])
  const [archivedAudios, setArchivedAudios] = useState<AudioFile[]>([])
  const [archiveLoading, setArchiveLoading] = useState(true)
  const [uploading,     setUploading]     = useState(false)
  const [isLoading,     setIsLoading]     = useState(true)
  const [deleteId,      setDeleteId]      = useState<number | null>(null)
  const [deleteAudioId, setDeleteAudioId] = useState<number | null>(null)
  const [viewMode,      setViewMode]      = useState<'list' | 'grid'>('grid')
  const [uploadPreview, setUploadPreview] = useState<UploadPreview | null>(null)

  // Folders (All tab) — client-side only; full backend wiring is a
  // follow-up. The dialog state lives here; the catalog of folders sits
  // in localStorage so the user's folders survive a refresh.
  const [newFolderOpen, setNewFolderOpen] = useState(false)
  // Folders + membership now live on the server (persist across browsers /
  // devices). Seeded empty and loaded on mount; a one-time migration lifts any
  // legacy localStorage folders up to the server. `folderMap` keeps the same
  // `${kind}:${id}` → folderId shape the filtering logic already expects.
  const [folders,       setFolders]       = useState<Folder[]>([])
  // Currently-opened folder. `null` = top-level (root listing). Drives
  // both the listing filter and the Upload action (uploads inside a
  // folder are auto-tagged to it via the asset↔folder map).
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null)
  const [folderMap, setFolderMap] = useState<Record<string, string>>({})

  // Re-pull the authoritative folder state (used after a write fails, to undo
  // the optimistic local guess).
  const refreshFolders = useCallback(() => {
    getFolders()
      .then(r => { setFolders(r.data.folders); setFolderMap(r.data.items) })
      .catch(() => { /* keep current */ })
  }, [])

  const updateFolderMap = useCallback((updater: (m: Record<string, string>) => Record<string, string>) => {
    setFolderMap(prev => updater(prev))
  }, [])

  const moveAssetToFolder = useCallback((kind: AssetKind, id: number | string, folderId: string | null) => {
    // Optimistic local update, then persist. On failure re-sync from the server.
    updateFolderMap(prev => {
      const next = { ...prev }
      const key = assetMapKey(kind, id)
      if (folderId === null) delete next[key]
      else next[key] = folderId
      return next
    })
    assignFolder({ folder_id: folderId, items: [{ kind, item_id: Number(id) }] })
      .catch(() => { toast.error(t('common.error', 'Something went wrong')); refreshFolders() })
  }, [updateFolderMap, refreshFolders, t])

  // Load folders on mount; migrate legacy per-browser folders the first time.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      // Track whether the server is actually reachable. We must NOT wipe the
      // legacy localStorage folders unless we've confirmed the server is the
      // authoritative source — otherwise a transient getFolders() failure on
      // first load would delete the operator's local folders for good.
      let serverOk = true
      let resp: { folders: Folder[]; items: Record<string, string> }
      try { resp = (await getFolders()).data }
      catch { serverOk = false; resp = { folders: [], items: {} } }

      // Only migrate into a fresh (empty) server catalog so re-opening on a
      // second device doesn't duplicate already-migrated folders.
      let migrationOk = true
      if (serverOk && resp.folders.length === 0) {
        const localFolders = loadFolders()
        const localMap = loadAssetFolderMap()
        if (localFolders.length > 0 || Object.keys(localMap).length > 0) {
          const idMap: Record<string, string> = {}
          for (const lf of localFolders) {
            try { const { data } = await createFolder({ name: lf.name }); idMap[lf.id] = data.id }
            catch { migrationOk = false }
          }
          const byFolder: Record<string, { kind: AssetKind; item_id: number }[]> = {}
          for (const [key, oldFid] of Object.entries(localMap)) {
            const newFid = idMap[oldFid]
            if (!newFid) continue
            const [kind, idStr] = key.split(':')
            ;(byFolder[newFid] ||= []).push({ kind: kind as AssetKind, item_id: Number(idStr) })
          }
          for (const [fid, items] of Object.entries(byFolder)) {
            try { await assignFolder({ folder_id: fid, items }) } catch { migrationOk = false }
          }
          try { resp = (await getFolders()).data } catch { /* keep what we have */ }
        }
      }
      // Clear the legacy keys only once the server is confirmed authoritative
      // AND any migration completed cleanly — never on a failed load, so the
      // local fallback survives to be migrated on the next successful open.
      if (serverOk && migrationOk) {
        try { localStorage.removeItem(FOLDERS_STORAGE_KEY); localStorage.removeItem(ASSET_FOLDER_MAP_KEY) } catch { /* ignore */ }
      }

      if (!cancelled) { setFolders(resp.folders); setFolderMap(resp.items) }
    })()
    return () => { cancelled = true }
  }, [])
  // Asset preview modal — single state for every kind of asset. null while
  // closed; set to one of `{ kind: 'media' | 'audio' | 'link', … }` from a
  // card's onPreview to open the modal with the matching controls.
  const [previewAsset, setPreviewAsset] = useState<PreviewAsset | null>(null)

  // The media asset to publish — drives the shared PublishDialog. Mirrors the
  // Audio Hub's streamTarget: a card's Publish action sets it, the dialog
  // pushes the asset straight to the chosen screens/groups.
  const [publishTarget, setPublishTarget] = useState<MediaFile | null>(null)
  // The interactive link to publish — drives a second PublishDialog (kind menu).
  const [publishLinkTarget, setPublishLinkTarget] = useState<InteractiveLink | null>(null)

  // Links (interactive menus rendered under the Links tab)
  const [links,           setLinks]           = useState<InteractiveLink[]>([])
  const [linksLoading,    setLinksLoading]    = useState(true)
  const [linkDialogOpen,  setLinkDialogOpen]  = useState(false)
  const [editingLink,     setEditingLink]     = useState<InteractiveLink | null>(null)
  const [deleteLinkId,    setDeleteLinkId]    = useState<number | null>(null)
  const [thumbCache,    setThumbCache]    = useState<Record<number, string>>({})
  const [renameFile,    setRenameFile]    = useState<MediaFile | null>(null)

  // Playlists state
  const [playlists,       setPlaylists]       = useState<Playlist[]>([])
  const [playlistsLoading, setPlaylistsLoading] = useState(true)
  const [playlistDialog,  setPlaylistDialog]  = useState<{ mode: 'create' | 'edit'; playlist: Playlist | null } | null>(null)
  const [deletePlaylistId, setDeletePlaylistId] = useState<number | null>(null)

  const inputRef = useRef<HTMLInputElement>(null)

  const [search,      setSearch]      = useState('')
  const [filterType,  setFilterType]  = useState('all')
  const [filterDate,  setFilterDate]  = useState('all')
  const [sortOrder,   setSortOrder]   = useState<'newest' | 'oldest'>('newest')
  const [products,    setProducts]    = useState<ProductLite[]>([])
  const [brandUrlToSlot, setBrandUrlToSlot] = useState<Record<string, string>>({})

  const loadMedia = useCallback(() => {
    getMedia(false, 'signage').then(r => {
      const mediaList: MediaFile[] = r.data
      setFiles(mediaList)
      setIsLoading(false)

      // Restore thumbnails from localStorage immediately (instant display on reload)
      const fromStorage: Record<number, string> = {}
      mediaList.filter(f => f.file_type.startsWith('video')).forEach(f => {
        const stored = localStorage.getItem(`${THUMB_CACHE_PREFIX}${f.id}`)
        if (stored) fromStorage[f.id] = stored
      })
      if (Object.keys(fromStorage).length > 0) setThumbCache(fromStorage)

      // Generate thumbnails only for videos not yet cached
      mediaList
        .filter(f => f.file_type.startsWith('video') && !fromStorage[f.id])
        .forEach(f => {
          const url = f.url.startsWith('http') ? f.url : `${BACKEND_URL}${f.url}`
          captureVideoThumbnail(url).then(({ preview: thumb }) => {
            if (thumb) {
              try { localStorage.setItem(`${THUMB_CACHE_PREFIX}${f.id}`, thumb) } catch {}
              setThumbCache(prev => ({ ...prev, [f.id]: thumb }))
            }
          })
        })
    })
  }, [])

  const loadPlaylists = useCallback(() => {
    getPlaylists().then(r => {
      setPlaylists(r.data)
      setPlaylistsLoading(false)
    })
  }, [])

  // Archive listing — archived media + audio fetched together. Run on mount so
  // the Archive tab badge is populated before the operator opens the tab.
  const loadArchive = useCallback(() => {
    setArchiveLoading(true)
    Promise.all([getMedia(true, 'signage'), getAudios(true)])
      .then(([m, a]) => { setArchivedFiles(m.data); setArchivedAudios(a.data) })
      .catch(() => {})
      .finally(() => setArchiveLoading(false))
  }, [])

  // Links are stored as Menu rows with type='interactive' (the visual menu
  // that shared the table was dropped), so list /menus and keep those.
  const loadLinks = useCallback(() => {
    setLinksLoading(true)
    api.get('/menus')
      .then(r => {
        const interactives = (r.data as any[])
          .filter(m => m.type === 'interactive')
          .map(m => ({
            id: m.id,
            name: m.name,
            interactive_link: m.interactive_link ?? '',
            show_qr: !!m.show_qr,
            qr_position: (m.qr_position as QrPosition) ?? 'bottom-right',
          }))
        setLinks(interactives)
      })
      .catch(() => {})
      .finally(() => setLinksLoading(false))
  }, [])

  useEffect(() => { loadMedia() }, [loadMedia])
  useEffect(() => { loadPlaylists() }, [loadPlaylists])
  useEffect(() => { loadLinks() }, [loadLinks])
  useEffect(() => { loadArchive() }, [loadArchive])
  useEffect(() => { getProducts().then(r => setProducts(r.data)).catch(() => {}) }, [])
  useEffect(() => {
    getBrandAssets().then(r => {
      const map: Record<string, string> = {}
      for (const a of r.data as { slot_key: string; url: string | null }[]) {
        if (a.url) map[normalizeMediaUrl(a.url)] = a.slot_key
      }
      setBrandUrlToSlot(map)
    }).catch(() => {})
  }, [])

  const handleFileSelected = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (inputRef.current) inputRef.current.value = ''
    const limitError = uploadLimitError(file)
    if (limitError) {
      toast.error(limitError)
      return
    }
    // Every asset type now flows through the single UploadConfirmDialog: images
    // crop inline, videos capture a thumbnail frame, PDFs pick document/folder
    // mode, audio previews inline. We just stage the file + any preview here.
    let preview: string | null = null
    let duration = 0
    if (file.type.startsWith('video')) {
      const res = await captureVideoThumbnail(file)
      preview = res.preview
      duration = res.duration
    } else if (file.type === 'image/gif') {
      // GIFs aren't cropped (crop would flatten the animation); pass a data URL
      // so the modal can preview the animated frame directly.
      preview = await new Promise<string | null>(resolve => {
        const reader = new FileReader()
        reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null)
        reader.onerror = () => resolve(null)
        reader.readAsDataURL(file)
      })
    }
    setUploadPreview({ file, preview, duration })
  }, [])

  // Core upload: push the file, apply name/duration, file it into the current
  // folder, cache a video thumbnail. Shared by the image crop flow and the
  // video/PDF UploadConfirmDialog so both go through one code path.
  const doUpload = useCallback(async (
    file: File,
    opts?: { name?: string; duration?: number; videoPreview?: string | null },
  ) => {
    setUploading(true)
    try {
      const { data } = await runUpload({
        label: file.name, kind: 'media',
        upload: (onProgress) => uploadMedia(file, onProgress),
      })
      const finalName = (opts?.name ?? '').trim() || file.name
      const patchData: Record<string, any> = {}
      if (finalName !== data.original_name) patchData.original_name = finalName
      if (file.type.startsWith('video') && opts?.duration && opts.duration > 0) patchData.duration = Math.round(opts.duration)
      if (Object.keys(patchData).length > 0) {
        await updateMedia(data.id, patchData as any, { initial: true })
        if (patchData.original_name) data.original_name = patchData.original_name
        if (patchData.duration) data.duration = patchData.duration
      }
      setFiles(prev => [data, ...prev])
      if (currentFolderId) moveAssetToFolder('m', data.id, currentFolderId)
      if (file.type.startsWith('video') && opts?.videoPreview) {
        setThumbCache(prev => ({ ...prev, [data.id]: opts.videoPreview! }))
        try { localStorage.setItem(`${THUMB_CACHE_PREFIX}${data.id}`, opts.videoPreview!) } catch {}
      }
      toast.success(t('common.uploadSuccess', 'File uploaded successfully'))
    } catch (err: any) {
      toast.error(err.response?.data?.detail || err.message || t('common.uploadFailed', 'Upload failed'))
    } finally {
      setUploading(false)
    }
  }, [currentFolderId, moveAssetToFolder, t, runUpload])

  // PDF upload: store the PDF as a single paged document (raw upload), or
  // rasterize each page server-side into images filed under a new folder.
  const uploadPdf = useCallback(async (file: File, name: string, mode: PdfUploadMode) => {
    setUploading(true)
    try {
      if (mode === 'folder') {
        // Server renders each page → an image MediaFile; we create a folder and
        // file every page into it, so the PDF lands as a "folder of images".
        const { data } = await createMediaFromPdf(file, name || file.name)
        const pages = data as MediaFile[]
        const base = (name.trim() || file.name.replace(/\.pdf$/i, '')) || 'PDF'
        const { data: folder } = await createFolder({ name: base })
        await assignFolder({ folder_id: folder.id, items: pages.map(p => ({ kind: 'm' as const, item_id: p.id })) })
        setFolders(prev => [folder, ...prev])
        setFolderMap(prev => {
          const m = { ...prev }
          pages.forEach(p => { m[assetMapKey('m', p.id)] = folder.id })
          return m
        })
        setFiles(prev => [...pages, ...prev])
        toast.success(t('assets.pdfFoldered', {
          defaultValue: 'PDF added as a folder of {{count}} images.',
          count: pages.length,
        }))
      } else {
        const { data } = await runUpload({
          label: file.name, kind: 'media',
          upload: (onProgress) => uploadMedia(file, onProgress),
        })
        const finalName = name.trim() || file.name
        if (finalName !== data.original_name) {
          await updateMedia(data.id, { original_name: finalName }, { initial: true })
          data.original_name = finalName
        }
        setFiles(prev => [data, ...prev])
        if (currentFolderId) moveAssetToFolder('m', data.id, currentFolderId)
        toast.success(t('assets.pdfUploaded', 'PDF uploaded.'))
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || err?.message || t('common.uploadFailed', 'Upload failed'))
    } finally {
      setUploading(false)
    }
  }, [t, currentFolderId, moveAssetToFolder, runUpload])

  // Import a video's audio track into the Audio library instead of adding the
  // clip to Media. The file is uploaded as-is to the audio endpoint (no
  // re-encode — the backend has no ffmpeg); players play only its audio track
  // because audio assets feed the manifest's parallel audio bed. The result
  // lands in the Audio Hub (MediaPage doesn't list live audio), so we just
  // toast + rename.
  const uploadVideoAsAudio = useCallback(async (file: File, name: string) => {
    setUploading(true)
    try {
      const { data } = await runUpload({
        label: file.name, kind: 'audio',
        upload: (onProgress) => uploadAudio(file, onProgress),
      })
      const finalName = name.trim() || file.name
      if (finalName !== data.original_name) {
        await updateAudio(data.id, { original_name: finalName })
      }
      toast.success(t('assets.videoAsAudioDone', 'Audio added to your Audio library.'))
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || err?.message || t('common.uploadFailed', 'Upload failed'))
    } finally {
      setUploading(false)
    }
  }, [runUpload, t])

  // Single upload-confirm handler for every asset type. Images arrive already
  // cropped; PDFs carry a mode (document / folder); video keeps its captured
  // thumbnail (or, when asAudio is set, imports only its audio track). Branch
  // on the staged file so PDFs take the rasterize path.
  const handleConfirmUpload = useCallback(async (
    result: { name: string; file: File; pdfMode?: PdfUploadMode; asAudio?: boolean },
  ) => {
    if (!uploadPreview) return
    const staged = uploadPreview
    const isPdf = staged.file.type === 'application/pdf' || /\.pdf$/i.test(staged.file.name)
    if (isPdf) {
      await uploadPdf(result.file, result.name, result.pdfMode ?? 'document')
    } else if (result.asAudio) {
      await uploadVideoAsAudio(result.file, result.name)
    } else {
      await doUpload(result.file, { name: result.name, duration: staged.duration, videoPreview: staged.preview })
    }
    setUploadPreview(current => current === staged ? null : current)
  }, [uploadPreview, doUpload, uploadPdf, uploadVideoAsAudio])

  // Permanent delete — only reachable from the Archive tab, so it targets the
  // archived list. The backend rejects deleting a non-archived row (409).
  const removeAudio = async () => {
    if (deleteAudioId === null) return
    try {
      await deleteAudio(deleteAudioId)
      setArchivedAudios(prev => prev.filter(a => a.id !== deleteAudioId))
      toast.success(t('common.deleted', 'Deleted'))
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || t('common.deleteFailed', 'Failed to delete'))
    } finally {
      setDeleteAudioId(null)
    }
  }

  // Archive (soft-delete) — the library "trash" action. Moves the item out of
  // the live list and into the Archive; fully reversible via restore.
  const handleArchiveMedia = async (id: number) => {
    const f = files.find(x => x.id === id)
    try {
      await archiveMedia(id)
      setFiles(prev => prev.filter(x => x.id !== id))
      if (f) setArchivedFiles(prev => [{ ...f, archived_at: new Date().toISOString() }, ...prev])
      toast.success(t('assets.movedToArchive', 'Moved to Archive'))
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || t('common.error', 'Something went wrong'))
    }
  }

  const handleRestoreMedia = async (id: number) => {
    const f = archivedFiles.find(x => x.id === id)
    try {
      await restoreMedia(id)
      setArchivedFiles(prev => prev.filter(x => x.id !== id))
      if (f) setFiles(prev => [{ ...f, archived_at: null }, ...prev])
      toast.success(t('assets.restored', 'Restored from Archive'))
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || t('common.error', 'Something went wrong'))
    }
  }

  // Restore an archived audio track. The track reappears in the Audio Hub
  // (which fetches live audio on mount) — MediaPage no longer renders live
  // audio, so we only drop it from the archived list here.
  const handleRestoreAudio = async (id: number) => {
    try {
      await restoreAudio(id)
      setArchivedAudios(prev => prev.filter(x => x.id !== id))
      toast.success(t('assets.restored', 'Restored from Archive'))
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || t('common.error', 'Something went wrong'))
    }
  }

  const handleRename = async (name: string) => {
    if (!renameFile) return
    await updateMedia(renameFile.id, { original_name: name })
    setFiles(prev => prev.map(f => f.id === renameFile.id ? { ...f, original_name: name } : f))
    setRenameFile(null)
    toast.success(t('common.renamedSuccess', 'Renamed successfully'))
  }

  // Permanent delete — Archive tab only; targets the archived list.
  const remove = async () => {
    if (deleteId === null) return
    try {
      await deleteMedia(deleteId)
      setArchivedFiles(prev => prev.filter(f => f.id !== deleteId))
      toast.success(t('common.deleted', 'Deleted'))
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || t('common.deleteFailed', 'Failed to delete'))
    } finally {
      setDeleteId(null)
    }
  }

  const handleSavePlaylist = async (
    name: string, description: string,
    mediaIds: number[], mediaDurations: Record<number, number>,
    linkIds: number[], linkDurations: Record<number, number>,
    mediaOrder: Record<number, number>, linkOrder: Record<number, number>,
    event: EventFields,
    companions: Companions,
  ): Promise<void> => {
    if (!playlistDialog) return
    try {
      const basePayload = {
        name,
        description: description || undefined,
        media_ids: mediaIds,
        media_durations: mediaDurations,
        link_ids: linkIds,
        link_durations: linkDurations,
        media_order: mediaOrder,
        link_order: linkOrder,
        ...event,
        ...companionsToPayload(companions),
      }
      if (playlistDialog.mode === 'create') {
        const { data } = await createPlaylist(basePayload)
        setPlaylists(prev => [data, ...prev])
        toast.success(t('common.playlistCreated', 'Playlist created'))
      } else if (playlistDialog.playlist) {
        const { data } = await updatePlaylist(playlistDialog.playlist.id, basePayload)
        setPlaylists(prev => prev.map(p => p.id === data.id ? data : p))
        toast.success(t('common.playlistUpdated', 'Playlist updated'))
      }
      setPlaylistDialog(null)
    } catch (err: any) {
      toast.error(err.response?.data?.detail || t('common.playlistSaveFailed', 'Failed to save playlist'))
      throw err
    }
  }

  const handleDeletePlaylist = async () => {
    if (deletePlaylistId === null) return
    await deletePlaylist(deletePlaylistId)
    setPlaylists(prev => prev.filter(p => p.id !== deletePlaylistId))
    setDeletePlaylistId(null)
  }

  const isProductView = filterType === 'product'
  const isBrandView   = filterType === 'brand'
  // Count of PDF-type assets — feeds the Documents tab badge.
  const pdfCount = files.filter(f => f.file_type === 'application/pdf' || /\.pdf$/i.test(f.original_name)).length

  // Folder membership helpers — `folderMap` is keyed by `${kind}:${id}` so we
  // can ask "what folder is this asset in?" with one lookup.
  const folderOfMedia = useCallback((id: number) => folderMap[assetMapKey('m', id)] ?? null, [folderMap])
  const folderOfLink  = useCallback((id: number) => folderMap[assetMapKey('l', id)] ?? null, [folderMap])

  // Counts shown inside FolderCards: number of items currently filed under
  // each folder across all asset kinds.
  const folderItemCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const key of Object.values(folderMap)) {
      counts[key] = (counts[key] ?? 0) + 1
    }
    return counts
  }, [folderMap])

  const filtered = isProductView ? [] : (() => {
    const base = files.filter(f => {
      const matchName = f.original_name.toLowerCase().includes(search.toLowerCase())
      const matchType =
        filterType === 'all' ||
        (filterType === 'image' && f.file_type.startsWith('image') && f.file_type !== 'image/gif') ||
        (filterType === 'gif'   && f.file_type === 'image/gif') ||
        (filterType === 'video' && f.file_type.startsWith('video')) ||
        (filterType === 'brand' && !!brandUrlToSlot[normalizeMediaUrl(f.url)]) ||
        (filterType === 'other' && !f.file_type.startsWith('image') && !f.file_type.startsWith('video'))
      // Tab-driven hard filter on top of any in-tab refinement:
      //   media     → images + gifs + videos only
      //   documents → PDFs only
      //   all       → everything (no extra restriction)
      const fileIsPdf = f.file_type === 'application/pdf' || /\.pdf$/i.test(f.original_name)
      let matchTab = true
      if (activeTab === 'media')      matchTab = f.file_type.startsWith('image') || f.file_type.startsWith('video')
      else if (activeTab === 'documents') matchTab = fileIsPdf
      const now = new Date()
      const created = new Date(f.created_at)
      let matchDate = true
      if (!isBrandView) {
        if (filterDate === 'today') matchDate = created.toDateString() === now.toDateString()
        else if (filterDate === 'week') matchDate = created >= new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
        else if (filterDate === 'month') matchDate = created >= new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
      }
      // Folder filter: when the user is inside a folder, only show items
      // tagged with that folder. At the root, only show items NOT in any
      // folder (so the folders are the entry points to nested content).
      const folderId = folderOfMedia(f.id)
      const matchFolder = currentFolderId
        ? folderId === currentFolderId
        : folderId === null
      return matchName && matchType && matchTab && matchDate && matchFolder
    })
    return [...base].sort((a, b) => {
      const diff = new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      return sortOrder === 'newest' ? diff : -diff
    })
  })()

  const filteredProducts = isProductView
    ? products.filter(p => p.image_url && p.name.toLowerCase().includes(search.toLowerCase()))
    : []

  // ── Pagination — page through assets (no page scroll), like the dashboard.
  // One page state, reset whenever the view changes.
  const ASSETS_PAGE_SIZE = 12
  const [page, setPage] = useState(1)
  useEffect(() => { setPage(1) }, [activeTab, search, filterType, filterDate, currentFolderId, sortOrder])

  // Archive items filtered/sorted once here so the pager total and the rendered
  // slice agree; media + audio share a single paged sequence.
  const archiveQ = search.trim().toLowerCase()
  const archiveAll = [
    ...archivedFiles
      .filter(f => !archiveQ || f.original_name.toLowerCase().includes(archiveQ))
      .sort((a, b) => new Date(b.archived_at ?? b.created_at).getTime() - new Date(a.archived_at ?? a.created_at).getTime())
      .map(f => ({ k: 'm' as const, f })),
    ...archivedAudios
      .filter(a => !archiveQ || a.original_name.toLowerCase().includes(archiveQ))
      .sort((a, b) => new Date(b.archived_at ?? b.created_at).getTime() - new Date(a.archived_at ?? a.created_at).getTime())
      .map(f => ({ k: 'a' as const, f })),
  ]

  const activeTotal =
    activeTab === 'links'    ? links.length
    : activeTab === 'archive'  ? archiveAll.length
    : isProductView          ? filteredProducts.length
    :                          filtered.length
  const totalPages = Math.max(1, Math.ceil(activeTotal / ASSETS_PAGE_SIZE))
  const curPage    = Math.min(page, totalPages)
  const pageStart  = (curPage - 1) * ASSETS_PAGE_SIZE
  function pageSlice<T>(arr: T[]): T[] { return arr.slice(pageStart, pageStart + ASSETS_PAGE_SIZE) }

  return (
    // Bounded to the shell panel: the tab strip stays pinned and the asset
    // content scrolls inside its own region, so the page itself never scrolls.
    <div className="flex flex-col gap-6 flex-1 min-h-0">
      {/* Tab strip is the shared AssetTabs (used by BrandAssetsPage too).
          MediaPage owns All / Media / Documents / Links via local state;
          AssetTabs is told about that via the onLocalChange callback. The
          Brand Assets tab inside AssetTabs always navigates away. */}
      <AssetTabs
        active={activeTab}
        onLocalChange={(tab) => { setActiveTab(tab); setCurrentFolderId(null) }}
        allCount={files.length + links.length}
        mediaCount={files.filter(f => f.file_type.startsWith('image') || f.file_type.startsWith('video')).length}
        docCount={pdfCount}
        linkCount={links.length}
        archiveCount={archivedFiles.length + archivedAudios.length}
      />

      {/* Content region — everything below the pinned tabs. Paged (not
          scrolled) so the page never shows a scrollbar; a pager sits at the end. */}
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col gap-6">
      {/* Folder breadcrumb — only visible when the user is inside a folder.
          Acts as the back affordance + tells the operator which folder
          contextual upload + drop targets will land in. */}
      {currentFolderId && (
        <div className="flex items-center gap-2 text-sm">
          <button
            type="button"
            onClick={() => setCurrentFolderId(null)}
            className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            {t('assets.allFolders', 'All folders')}
          </button>
          <span className="text-muted-foreground">/</span>
          <span className="font-medium">
            {folders.find(f => f.id === currentFolderId)?.name ?? t('common.folder', 'Folder')}
          </span>
        </div>
      )}

      {/* Action button row — sits directly under the tabs bar, right-aligned.
          Per-tab buttons (New Folder / Upload / Create Link). The New Folder
          and Upload buttons stay put even when the user is inside a folder
          so the action surface doesn't reflow on navigation; uploads from a
          folder still auto-tag into it via currentFolderId. */}
      <div className="flex items-center justify-end gap-2">
        {/* New Folder — All tab. */}
        {canUpload && activeTab === 'all' && (
          <Button
            size="sm" variant="outline" className="gap-2 h-10"
            onClick={() => setNewFolderOpen(true)}
          >
            <FolderPlus className="w-4 h-4" />
            {t('assets.newFolder', 'New Folder')}
          </Button>
        )}
        {/* Upload — All / Media / Documents tabs. The accept= filter narrows
            the OS picker to types that make sense for the tab. Audio is
            uploaded from the Audio Hub now, so it's excluded here. */}
        {canUpload && (activeTab === 'all' || activeTab === 'media' || activeTab === 'documents') && (
          <>
            <Button size="sm" className="gap-2 h-10 max-sm:hidden" disabled={uploading} onClick={() => inputRef.current?.click()}>
              <Upload className="w-4 h-4" />
              {uploading ? t('common.loading') : t('assets.upload', 'Upload')}
            </Button>
            {/* Phone: the same Upload as a floating button above the tab bar,
                where the thumb already is. */}
            <Button
              className="sm:hidden fixed z-30 end-5 bottom-[calc(6rem+env(safe-area-inset-bottom))] h-14 w-14 rounded-full p-0 shadow-lg"
              disabled={uploading}
              onClick={() => inputRef.current?.click()}
              aria-label={t('assets.upload', 'Upload')}
            >
              {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Upload className="w-6 h-6" />}
            </Button>
            <input ref={inputRef} type="file" hidden onChange={handleFileSelected}
              accept={
                activeTab === 'documents' ? 'application/pdf'
                : 'image/*,video/mp4,video/webm,video/quicktime,application/pdf'
              } />
          </>
        )}
        {/* Create Link — Links tab only */}
        {activeTab === 'links' && (
          <Button
            size="sm" className="gap-2 h-10"
            onClick={() => { setEditingLink(null); setLinkDialogOpen(true) }}
          >
            <Plus className="w-4 h-4" />
            {t('assets.createLink', 'Create Webpage')}
          </Button>
        )}
      </div>

      {/* ── ALL + MEDIA + DOCUMENTS share the unified asset grid ────
          Filters (matchTab) take care of the type-narrowing per tab. ── */}
      {(activeTab === 'all' || activeTab === 'media' || activeTab === 'documents') && (
        <>
          {/* Combined search + filter row — search bar flex-fills on the
              left, type / date / sort / view controls sit on the right.
              The search input gets a red focus border + white fill so it
              clearly reads as "active" against the dark page background. */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('common.search', 'Search')}
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="ps-9 h-9 transition-colors focus-visible:border-primary focus-visible:ring-primary/30 focus-visible:text-foreground"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap shrink-0">
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[130px] bg-background h-9"><SelectValue placeholder={t('common.type', 'Type')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('screens.allTypes', 'All Types')}</SelectItem>
                  <SelectItem value="image">{t('screens.images', 'Images')}</SelectItem>
                  <SelectItem value="gif">{t('media.gifs', 'GIFs')}</SelectItem>
                  <SelectItem value="video">{t('screens.videos', 'Videos')}</SelectItem>
                  <SelectItem value="product">{t('nav.products', 'Products')}</SelectItem>
                  <SelectItem value="brand">{t('nav.brandAssets', 'Brand Assets')}</SelectItem>
                  <SelectItem value="other">{t('common.other', 'Other')}</SelectItem>
                </SelectContent>
              </Select>
              {!isProductView && !isBrandView && (
                <Select value={filterDate} onValueChange={setFilterDate}>
                  <SelectTrigger className="w-[140px] bg-background h-9"><SelectValue placeholder={t('dashboard.dateFilter', 'Date')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('common.allTime', 'All Time')}</SelectItem>
                    <SelectItem value="today">{t('common.today', 'Today')}</SelectItem>
                    <SelectItem value="week">{t('common.thisWeek', 'This Week')}</SelectItem>
                    <SelectItem value="month">{t('common.thisMonth', 'This Month')}</SelectItem>
                  </SelectContent>
                </Select>
              )}
              {!isProductView && !isBrandView && (
                <div className="flex items-center rounded-lg border border-border bg-background overflow-hidden shrink-0">
                  <button
                    onClick={() => setSortOrder('newest')}
                    className={`px-2.5 h-9 text-xs font-medium transition-colors ${sortOrder === 'newest' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    {t('mediaPicker.sortNewest', 'Newest')}
                  </button>
                  <button
                    onClick={() => setSortOrder('oldest')}
                    className={`px-2.5 h-9 text-xs font-medium transition-colors ${sortOrder === 'oldest' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    {t('mediaPicker.sortOldest', 'Oldest')}
                  </button>
                </div>
              )}
              <div className="flex items-center rounded-lg border border-border bg-background overflow-hidden shrink-0">
                <button onClick={() => setViewMode('list')} className={`h-9 px-2 flex items-center justify-center transition-colors ${viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`} title={t('common.listView', 'List view')}>
                  <List className="w-4 h-4" />
                </button>
                <button onClick={() => setViewMode('grid')} className={`h-9 px-2 flex items-center justify-center transition-colors ${viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`} title={t('common.gridView', 'Grid view')}>
                  <LayoutGrid className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Content View */}
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {isLoading ? (
                Array.from({ length: 10 }).map((_, i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="w-full aspect-video rounded-xl" />
                    <Skeleton className="h-3 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                ))
              ) : isProductView ? (
                filteredProducts.length === 0 ? (
                  <EmptyState
                    className="col-span-full"
                    icon={Package}
                    title={search ? t('assets.noProductsMatch', 'No matching products') : t('assets.noProductsTitle', 'No product images yet')}
                    description={search ? undefined : t('assets.noProductsDesc', 'Products that have an image show up here, ready to drop into your signage content.')}
                  />
                ) : (
                  pageSlice(filteredProducts).map(p => (
                    <div key={p.id} className="group relative rounded-xl overflow-hidden bg-card transition-shadow">
                      <a href={p.image_url!} target="_blank" rel="noreferrer" className="block w-full aspect-video bg-muted overflow-hidden">
                        <img src={p.image_url!} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                        <div className="absolute top-2 start-2">
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-orange-500/80 text-white backdrop-blur-sm">
                            <Package className="w-2.5 h-2.5" /> Product
                          </span>
                        </div>
                      </a>
                      <div className="p-2">
                        <p className="text-xs font-medium truncate">{p.name}</p>
                      </div>
                    </div>
                  ))
                )
              ) : (() => {
                // On the All tab, folder cards lead the grid, then files,
                // audio, then links — every asset type appears in the same
                // unified grid (matches the "All page must show links,
                // documents, and audio like media" requirement). Folders only
                // appear at the root since we don't nest folders yet.
                const folderCardsForAll = activeTab === 'all' && !currentFolderId
                  ? folders
                      .filter(f => !search.trim() || f.name.toLowerCase().includes(search.toLowerCase()))
                      .map(folder => (
                        <FolderCard
                          key={`folder-${folder.id}`}
                          folder={folder}
                          count={folderItemCounts[folder.id] ?? 0}
                          onOpen={() => setCurrentFolderId(folder.id)}
                          onDropAsset={({ kind, id }) => {
                            moveAssetToFolder(kind, id, folder.id)
                            toast.success(t('assets.movedToFolder', { defaultValue: 'Moved to {{name}}', name: folder.name }))
                          }}
                          onDelete={async () => {
                            // Optimistic: drop the folder + detach its assets so
                            // they surface at the root again. The server cascades
                            // the membership rows; the assets themselves stay.
                            setFolders(prev => prev.filter(x => x.id !== folder.id))
                            updateFolderMap(prev => {
                              const m = { ...prev }
                              for (const k of Object.keys(m)) if (m[k] === folder.id) delete m[k]
                              return m
                            })
                            try { await deleteFolder(folder.id) }
                            catch { toast.error(t('common.error', 'Something went wrong')); refreshFolders() }
                          }}
                        />
                      ))
                  : []
                const linkCardsForAll = activeTab === 'all'
                  ? links
                      .filter(l => !search.trim() || l.name.toLowerCase().includes(search.toLowerCase()) || l.interactive_link.toLowerCase().includes(search.toLowerCase()))
                      .filter(l => {
                        const fid = folderOfLink(l.id)
                        return currentFolderId ? fid === currentFolderId : fid === null
                      })
                      .map(link => (
                        <LinkCard
                          key={`link-${link.id}`}
                          link={link}
                          onEdit={() => { setEditingLink(link); setLinkDialogOpen(true) }}
                          onDelete={() => setDeleteLinkId(link.id)}
                          onPreview={() => setPreviewAsset({ kind: 'link', link })}
                          onPublish={() => setPublishLinkTarget(link)}
                        />
                      ))
                  : []
                if (filtered.length === 0 && linkCardsForAll.length === 0 && folderCardsForAll.length === 0) {
                  return isBrandView ? (
                    <EmptyState
                      className="col-span-full"
                      icon={Layers}
                      title={t('assets.noBrandTitle', 'No brand assets yet')}
                      description={t('assets.noBrandDesc', 'Upload your logos and brand imagery so they’re ready to use across your content.')}
                    />
                  ) : (
                    <EmptyState
                      className="col-span-full"
                      icon={search.trim() ? Search : Upload}
                      title={
                        search.trim()          ? t('assets.noMatchingAssets', 'No matching assets')
                        : activeTab === 'media'     ? t('assets.noMediaTitle', 'No media yet')
                        : activeTab === 'documents' ? t('assets.noDocsTitle', 'No documents yet')
                        :                             t('assets.noAssetsTitle', 'No assets yet')
                      }
                      description={
                        search.trim()          ? undefined
                        : activeTab === 'media'     ? t('assets.noMediaDesc', 'Upload images and videos to use in your playlists and layouts.')
                        : activeTab === 'documents' ? t('assets.noDocsDesc', 'Upload PDFs to display as documents on your screens.')
                        :                             t('assets.noAssetsDesc', 'Upload media, save webpages, or create folders to build your content library.')
                      }
                    />
                  )
                }
                return (
                  <>
                    {folderCardsForAll}
                    {pageSlice(filtered).map(f => (
                      <GridCard
                        key={f.id} file={f} thumbCache={thumbCache}
                        draggable
                        onDragStart={e => {
                          e.dataTransfer.effectAllowed = 'move'
                          e.dataTransfer.setData('application/x-meno-asset', JSON.stringify({ kind: 'm', id: f.id }))
                        }}
                        onDelete={() => handleArchiveMedia(f.id)} onRename={() => setRenameFile(f)}
                        onPreview={() => setPreviewAsset({ kind: 'media', file: f })}
                        onPublish={() => setPublishTarget(f)}
                        brandSlot={isBrandView ? brandUrlToSlot[normalizeMediaUrl(f.url)] : undefined}
                      />
                    ))}
                    {linkCardsForAll}
                  </>
                )
              })()}
            </div>
          ) : (
            <Card className="bg-card">
              <CardContent className="p-0">
                <Table className="min-w-[760px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-20">{t('common.preview', 'Preview')}</TableHead>
                      <TableHead>{isProductView ? t('common.product', 'Product') : t('media.fileName')}</TableHead>
                      {!isProductView && <TableHead>{t('common.type', 'Type')}</TableHead>}
                      {isBrandView && <TableHead>{t('media.brandSlot', 'Brand Slot')}</TableHead>}
                      {!isProductView && <TableHead>{t('media.fileSize')}</TableHead>}
                      {!isProductView && <TableHead>{t('media.uploadedAt')}</TableHead>}
                      {!isProductView && <TableHead className="text-right">{t('common.actions')}</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <TableRow key={i}>
                          <TableCell><Skeleton className="h-10 w-16 rounded-md" /></TableCell>
                          <TableCell><Skeleton className="h-4 w-48 mb-1" /><Skeleton className="h-3 w-20" /></TableCell>
                          <TableCell><Skeleton className="h-5 w-14 rounded-full" /></TableCell>
                          <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                          <TableCell><Skeleton className="h-7 w-16 rounded-md" /></TableCell>
                        </TableRow>
                      ))
                    ) : isProductView ? (
                      filteredProducts.length === 0 ? (
                        <TableRow><TableCell colSpan={2} className="text-center text-muted-foreground py-24">{t('common.noData')}</TableCell></TableRow>
                      ) : (
                        pageSlice(filteredProducts).map(p => (
                          <TableRow key={p.id}>
                            <TableCell>
                              <div className="w-16 h-10 rounded-md overflow-hidden bg-muted border border-border/40">
                                <img src={p.image_url!} alt={p.name} className="w-full h-full object-cover" />
                              </div>
                            </TableCell>
                            <TableCell className="font-medium text-sm">{p.name}</TableCell>
                          </TableRow>
                        ))
                      )
                    ) : filtered.length === 0 ? (
                      <TableRow><TableCell colSpan={isBrandView ? 7 : 6} className="text-center text-muted-foreground py-24">{t('common.noData')}</TableCell></TableRow>
                    ) : (
                      pageSlice(filtered).map(f => {
                        const slot = isBrandView ? brandUrlToSlot[normalizeMediaUrl(f.url)] : undefined
                        return (
                          <TableRow key={f.id} className="group">
                            <TableCell>
                              <button
                                type="button"
                                onClick={() => setPreviewAsset({ kind: 'media', file: f })}
                                className="cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/30 rounded-md"
                                aria-label={t('common.preview', 'Preview')}
                              >
                                <MediaThumbnail file={f} thumbCache={thumbCache} />
                              </button>
                            </TableCell>
                            <TableCell>
                              <button
                                type="button"
                                onClick={() => setPreviewAsset({ kind: 'media', file: f })}
                                className="font-medium text-sm hover:text-primary transition-colors text-start"
                              >
                                {f.original_name}
                              </button>
                            </TableCell>
                            <TableCell><TypeBadge type={f.file_type} /></TableCell>
                            {isBrandView && (
                              <TableCell>
                                {slot ? (
                                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400">
                                    <Layers className="w-2.5 h-2.5" /> {SLOT_LABELS[slot] ?? slot}
                                  </span>
                                ) : '—'}
                              </TableCell>
                            )}
                            <TableCell className="text-muted-foreground text-sm">{f.file_size ? formatBytes(f.file_size) : '—'}</TableCell>
                            <TableCell className="text-muted-foreground text-sm">{formatDate(f.created_at, i18n.language)}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 touch-reveal transition-opacity">
                                <Button variant="ghost" size="icon" title={t('assets.publishToScreens', 'Publish to screens')} onClick={() => setPublishTarget(f)}>
                                  <Radio className="w-3.5 h-3.5" />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => setRenameFile(f)}>
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                <Button variant="ghost" size="icon" className="text-destructive" title={t('assets.archive', 'Archive')} onClick={() => handleArchiveMedia(f.id)}>
                                  <Archive className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        )
                      })
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Documents are now MediaFiles with file_type=pdf, rendered by the
          unified asset grid above (matchTab handles the filter). Playlists
          are no longer surfaced in the Asset → Documents tab. */}

      {/* ── LINKS TAB — link cards in their own grid (All tab already
          interleaves them with files above). ── */}
      {activeTab === 'links' && (
        <div className="space-y-4">
          {linksLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}
            </div>
          ) : links.length === 0 ? (
            <EmptyState
              icon={Link2}
              title={t('assets.linksEmptyTitle', 'No webpages yet')}
              description={t('assets.linksEmpty', 'Save external URLs (YouTube, Vimeo, web pages…) so screens can stream them without re-uploading.')}
            />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {pageSlice(links).map(link => (
                <div
                  key={link.id}
                  className="group flex items-center gap-3 p-3 rounded-xl border border-[hsl(var(--border-card,var(--border)))] bg-card hover:border-primary/40 transition-all cursor-pointer"
                  onClick={() => setPreviewAsset({ kind: 'link', link })}
                >
                  <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                    <Link2 className="w-5 h-5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground truncate">{link.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{link.interactive_link}</p>
                  </div>
                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 touch-reveal transition-opacity">
                    <Button
                      variant="ghost" size="icon"
                      className="w-8 h-8"
                      title={t('assets.publishToScreens', 'Publish to screens')}
                      onClick={(e) => { e.stopPropagation(); setPublishLinkTarget(link) }}
                    >
                      <Radio className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost" size="icon"
                      className="w-8 h-8 text-destructive"
                      onClick={(e) => { e.stopPropagation(); setDeleteLinkId(link.id) }}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── ARCHIVE TAB — soft-deleted media + audio. Restore brings an item
          back to the library; the trash here is the only path to permanent
          deletion (the backend rejects deleting a non-archived row). ── */}
      {activeTab === 'archive' && (
          <div className="space-y-4">
            {/* Search row — mirrors the library search for a familiar control. */}
            <div className="relative max-w-sm">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('common.search', 'Search')}
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="ps-9 h-9 transition-colors focus-visible:border-primary focus-visible:ring-primary/30 focus-visible:text-foreground"
              />
            </div>

            {/* Alarm banner — make the "permanent" nature unmistakable. */}
            <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <Trash2 className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{t('assets.archiveHint', 'Items here are no longer in your library. Restore them to use them again, or delete them permanently — permanent deletion cannot be undone.')}</span>
            </div>

            {archiveLoading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="w-full aspect-video rounded-xl" />
                    <Skeleton className="h-3 w-3/4" />
                  </div>
                ))}
              </div>
            ) : archiveAll.length === 0 ? (
              <EmptyState
                icon={Archive}
                iconClassName="text-muted-foreground"
                title={t('assets.archiveEmptyTitle', 'Archive is empty')}
                description={t('assets.archiveEmpty', 'Deleting an asset from the library moves it here first. Nothing is permanently removed until you delete it from the Archive.')}
              />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {pageSlice(archiveAll).map(item => item.k === 'm' ? (
                  <GridCard
                    key={`arch-m-${item.f.id}`}
                    file={item.f}
                    thumbCache={thumbCache}
                    onRestore={() => handleRestoreMedia(item.f.id)}
                    onDelete={() => setDeleteId(item.f.id)}
                    onRename={() => {}}
                    onPreview={() => setPreviewAsset({ kind: 'media', file: item.f })}
                  />
                ) : (
                  <AudioCard
                    key={`arch-a-${item.f.id}`}
                    file={item.f}
                    onRestore={() => handleRestoreAudio(item.f.id)}
                    onDelete={() => setDeleteAudioId(item.f.id)}
                    onRename={() => {}}
                    onPreview={() => setPreviewAsset({ kind: 'audio', file: item.f })}
                  />
                ))}
              </div>
            )}
          </div>
      )}

      {/* Pager — pages through the active tab's assets (mirrors the dashboard). */}
      {activeTotal > 0 && (
        <div className="shrink-0 mt-auto flex items-center justify-between gap-3 pt-3 border-t border-border">
          <p className="text-[11px] text-muted-foreground tabular-nums">
            {t('dashboard.showingRange', {
              defaultValue: 'Showing {{from}}–{{to}} of {{total}}',
              from: pageStart + 1,
              to: Math.min(pageStart + ASSETS_PAGE_SIZE, activeTotal),
              total: activeTotal,
            })}
          </p>
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline" size="sm" className="h-7 w-7 p-0"
              disabled={curPage <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              aria-label={t('common.previous', 'Previous')}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-[11px] tabular-nums text-muted-foreground px-1 min-w-[3rem] text-center">
              {curPage} / {totalPages}
            </span>
            <Button
              variant="outline" size="sm" className="h-7 w-7 p-0"
              disabled={curPage >= totalPages}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              aria-label={t('common.next', 'Next')}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
      </div>

      {/* Create / Edit link dialog */}
      <CreateLinkDialog
        open={linkDialogOpen}
        existing={editingLink}
        onClose={() => { setLinkDialogOpen(false); setEditingLink(null) }}
        onSaved={(saved) => {
          setLinks(prev => {
            const i = prev.findIndex(l => l.id === saved.id)
            if (i >= 0) {
              const next = [...prev]; next[i] = saved; return next
            }
            return [saved, ...prev]
          })
          toast.success(t('assets.linkSaved', 'Webpage saved'))
        }}
      />

      {/* Delete link confirm */}
      <ConfirmDialog
        open={deleteLinkId !== null}
        title={t('assets.deleteLinkTitle', 'Delete Webpage')}
        description={t('assets.deleteLinkDesc', 'This webpage will be removed from your asset library.')}
        onConfirm={async () => {
          if (deleteLinkId === null) return
          try {
            await deleteInteractiveMenu(deleteLinkId)
            setLinks(prev => prev.filter(l => l.id !== deleteLinkId))
            toast.success(t('common.deleted', 'Deleted'))
          } catch (e: any) {
            toast.error(e?.response?.data?.detail ?? t('common.deleteFailedGeneric', 'Failed to delete'))
          } finally {
            setDeleteLinkId(null)
          }
        }}
        onClose={() => setDeleteLinkId(null)}
      />

      {/* Upload confirmation */}
      {uploadPreview && (
        <UploadConfirmDialog
          preview={uploadPreview}
          onConfirm={handleConfirmUpload}
          onCancel={() => setUploadPreview(null)}
        />
      )}

      {/* New folder dialog */}
      {newFolderOpen && (
        <NewFolderDialog
          onCreate={async name => {
            try {
              const { data } = await createFolder({ name })
              setFolders(prev => [data, ...prev])
              setNewFolderOpen(false)
              toast.success(t('assets.folderCreated', 'Folder created'))
            } catch {
              toast.error(t('common.error', 'Something went wrong'))
            }
          }}
          onClose={() => setNewFolderOpen(false)}
        />
      )}

      {/* Rename dialog */}
      {renameFile && (
        <RenameDialog file={renameFile} onSave={handleRename} onClose={() => setRenameFile(null)} />
      )}

      {/* Delete audio confirm — archive permanent-delete only */}
      <ConfirmDialog
        open={deleteAudioId !== null}
        title={t('assets.deleteAudioTitle', 'Delete Audio')}
        description={t('assets.deleteAudioDesc', 'This audio track will be permanently removed.')}
        onConfirm={removeAudio}
        onClose={() => setDeleteAudioId(null)}
      />

      {/* Playlist create/edit dialog */}
      {playlistDialog && (
        <PlaylistDialog
          playlist={playlistDialog.playlist}
          allMedia={files}
          allLinks={links}
          thumbCache={thumbCache}
          onSave={handleSavePlaylist}
          onClose={() => setPlaylistDialog(null)}
        />
      )}

      <ConfirmDialog
        open={deleteId !== null}
        title={t('common.deleteMedia', 'Delete Media')}
        description={t('common.deleteMediaDesc', 'This file will be permanently removed.')}
        onConfirm={remove}
        onClose={() => setDeleteId(null)}
      />

      <ConfirmDialog
        open={deletePlaylistId !== null}
        title={t('common.deletePlaylist', 'Delete Playlist')}
        description={t('common.deletePlaylistDesc', 'This playlist will be removed. Media files are not deleted.')}
        onConfirm={handleDeletePlaylist}
        onClose={() => setDeletePlaylistId(null)}
      />

      {/* Publish content — reuses the shared PublishDialog (same one the Audio
          Hub streams with). Publishing pushes this single asset straight to the
          chosen screens/groups, replacing whatever they were showing; the dialog's
          "Schedule for later" toggle sends a window instead. */}
      <PublishDialog
        open={!!publishTarget}
        kind="media"
        publish={(payload) => publishMedia(publishTarget!.id, payload)}
        onClose={() => setPublishTarget(null)}
        onPublished={() => setPublishTarget(null)}
      />

      {/* Publish link — same dialog wired to the menu publish endpoint. */}
      <PublishDialog
        open={!!publishLinkTarget}
        kind="menu"
        publish={(payload) => publishMenu(publishLinkTarget!.id, payload)}
        onClose={() => setPublishLinkTarget(null)}
        onPublished={() => setPublishLinkTarget(null)}
      />

      {/* Asset preview modal — opens when a card's onPreview fires. The
          dialog renders kind-appropriate controls (image zoom, video / audio
          players, PDF iframe, link CTA). */}
      <AssetPreviewDialog asset={previewAsset} onClose={() => setPreviewAsset(null)} />
    </div>
  )
}
